/**
 * MktgHeading — reusable display-font heading for marketing sections.
 * Applies the .mktg-heading base class (League Spartan, weight 600,
 * line-height 1.1, letter-spacing -0.045em) plus optional size and
 * color variant classes defined in marketing-theme.css.
 * Sizes: hero (48–90px) | xl (40–64px) | lg (36–64px) | md (24–36px) | sm (20px)
 */

import React from 'react';
import { cn } from '@/lib/utils/utils';

type HeadingElement = 'h1' | 'h2' | 'h3' | 'h4' | 'p' | 'span';

/** Size maps to .mktg-heading-{size} CSS class */
type HeadingSize = 'hero' | 'xl' | 'lg' | 'md' | 'sm';

/** Color maps to .mktg-heading-{color} CSS class; 'dark' is the base default */
type HeadingColor = 'dark' | 'white' | 'yellow' | 'blue';

interface MktgHeadingProps {
  as?: HeadingElement;
  size?: HeadingSize;
  color?: HeadingColor;
  className?: string;
  style?: React.CSSProperties;
  children: React.ReactNode;
}

const sizeClass: Record<HeadingSize, string> = {
  hero: 'mktg-heading-hero',
  xl: 'mktg-heading-xl',
  lg: 'mktg-heading-lg',
  md: 'mktg-heading-md',
  sm: 'mktg-heading-sm'
};

const colorClass: Record<HeadingColor, string | undefined> = {
  dark: undefined, // default in .mktg-heading — no extra class needed
  white: 'mktg-heading-white',
  yellow: 'mktg-heading-yellow',
  blue: 'mktg-heading-blue'
};

export function MktgHeading({
  as: Component = 'h2',
  size = 'lg',
  color = 'dark',
  className,
  style,
  children
}: MktgHeadingProps) {
  return (
    <Component
      className={cn(
        'mktg-heading',
        sizeClass[size],
        colorClass[color],
        className
      )}
      style={style}
    >
      {children}
    </Component>
  );
}
